import { Expose } from 'class-transformer';

/**
 * Represents a patient contact from PCC
 * @see PatientContact schema in PCC API
 */
export class PccPatientContact {
	/**
	 * Unique identifier for the patient's contact
	 */
	contactId: number;

	/**
	 * Patient's contact's first name
	 */
	firstName: string;

	/**
	 * Patient's contact's last name
	 */
	lastName: string;

	/**
	 * Array of contact types (e.g. next of kin, guarantor). Values are configurable per facility
	 */
	contactType: string[];

	/**
	 * Relationship to the patient
	 */
	relationship: string;

	/**
	 * Street address for the patient's contact, if available
	 */
	addressLine1: string;

	/**
	 * Additional address information for the patient's contact, if available
	 */
	addressLine2: string;

	/**
	 * City for the patient's contact, if available
	 */
	city: string;

	/**
	 * Province or state for the patient's contact, if available
	 */
	state: string;

	/**
	 * Postal code for the patient's contact, if available
	 */
	postalCode: string;

	/**
	 * Home phone number for the patient's contact, if available
	 */
	homePhone: string;

	/**
	 * Cell phone number for the patient's contact, if available
	 */
	cellPhone: string;

	/**
	 * Email address for the patient's contact, if available
	 */
	email: string;

	@Expose()
	get fullName(): string {
		return [this.lastName, this.firstName].filter((val) => val).join(' ');
	}

	@Expose()
	get fullAddress(): string {
		return [this.addressLine1, this.city, this.state, this.postalCode].filter((val) => val).join(', ');
	}

	@Expose()
	get phone(): string {
		return this.cellPhone || this.homePhone || '';
	}

	@Expose()
	get phoneEmailDisplay(): string {
		const { phone } = this;
		if (!this.email) return phone;
		return `${phone}<br/>Email: ${this.email}`;
	}

	@Expose()
	get contactTypeDisplay(): string {
		if (!this.contactType) return '';
		return Array.isArray(this.contactType) ? this.contactType.join(', ') : this.contactType;
	}

	constructor(partial?: Partial<PccPatientContact>) {
		if (partial) Object.assign(this, partial);
	}
}

/**
 * Represents a user-defined field from PCC
 * @see PatientUserDefinedField schema in PCC API
 */
export class PccUserDefinedField {
	/**
	 * Name of the user defined field
	 */
	name: string;

	/**
	 * Value of the user defined field
	 */
	description: string;

	constructor(partial?: Partial<PccUserDefinedField>) {
		if (partial) Object.assign(this, partial);
	}
}

/**
 * Represents patient race information
 * @see PatientRace schema in PCC API
 */
export class PccPatientRace {
	/**
	 * Unique identifier for the race
	 */
	raceId: number;

	/**
	 * Patient's race description. Values standardized to meet ONC guidelines
	 */
	raceDesc: string;

	constructor(partial?: Partial<PccPatientRace>) {
		if (partial) Object.assign(this, partial);
	}
}

/**
 * Represents a patient address structure
 * @see PatientAddress schema in PCC API
 */
export class PccPatientAddress {
	/**
	 * Street address
	 */
	addressLine1: string;

	/**
	 * Additional address information
	 */
	addressLine2: string;

	/**
	 * City
	 */
	city: string;

	/**
	 * State/Province
	 */
	state: string;

	/**
	 * Postal Code
	 */
	postalCode: string;

	/**
	 * Country
	 */
	country: string;

	@Expose()
	get formatted(): string {
		return [this.addressLine1, this.addressLine2, this.city, this.state, this.postalCode].filter((val) => val).join(', ');
	}

	constructor(partial?: Partial<PccPatientAddress>) {
		if (partial) Object.assign(this, partial);
	}
}

/**
 * Represents a patient from PointClickCare
 * @see Patient schema in PCC API
 * @see https://connect2.pointclickcare.com/api/public/preview1/orgs/{orgUuid}/patients/{patientId}
 */
export class PccPatient {
	/**
	 * PointClickCare's unique identifier for the organization (numeric)
	 */
	orgId: number;

	/**
	 * PointClickCare's unique identifier for the organization (UUID)
	 */
	orgUuid: string;

	/**
	 * PointClickCare's unique identifier for a facility within the organization
	 */
	facId: number;

	/**
	 * PointClickCare's unique identifier for a patient within the organization
	 */
	patientId: number;

	/**
	 * Simpli360's unique identifier for a patient within the facility
	 */
	ntaPatientId?: string;

	/**
	 * The patient's medical record number, if available
	 */
	medicalRecordNumber: string;

	/**
	 * The Medicaid number for the patient. US facilities only
	 */
	medicaidNumber: string;

	/**
	 * The Medicare number for the patient. US facilities only
	 */
	medicareNumber: string;

	/**
	 * Medicare Beneficiary Identifier (replaces SSN-based Medicare Number)
	 */
	medicareBeneficiaryIdentifier: string;

	/**
	 * Patient's Social Security Number (US) or Social Insurance Number (Canada)
	 */
	socialBeneficiaryIdentifier?: string;

	/**
	 * Canadian Health Card Number. Ontario, Canada only
	 */
	canadianHCN?: string;

	/**
	 * System-generated identifier for resident across facilities in organization
	 */
	ompId: number;

	/**
	 * Patient's religion. Values configurable at facility level
	 */
	religion: string;

	/**
	 * Patient's marital status. Values configurable at facility level
	 */
	maritalStatus: string;

	/**
	 * Patient's ethnicity. Values configurable at facility level
	 */
	ethnicityDesc: string;

	/**
	 * Patient's occupation
	 */
	occupation: string;

	/**
	 * Patient's birth place
	 */
	birthPlace: string;

	/**
	 * Patient's citizenship. Values configurable at facility level
	 */
	citizenship: string;

	/**
	 * Patient's maiden name
	 */
	maidenName?: string;

	/**
	 * External identifier for patient, permissible for display in external systems
	 */
	patientExternalId: string;

	/**
	 * Patient status: "New", "Current", or "Discharged"
	 */
	patientStatus: string;

	/**
	 * PointClickCare's unique identifier for a bed within a facility
	 */
	bedId: number;

	/**
	 * The patient's current bed details
	 */
	bedDesc: string;

	/**
	 * The patient's current room details
	 */
	roomDesc: string;

	/**
	 * PointClickCare's unique identifier for a room within a facility
	 */
	roomId: number;

	/**
	 * Type of room rate (e.g. Semi Private, Private, Shared)
	 */
	roomType: string;

	/**
	 * The patient's current floor details
	 */
	floorDesc: string;

	/**
	 * PointClickCare's unique identifier for a floor within a facility
	 */
	floorId: number;

	/**
	 * The patient's current unit details
	 */
	unitDesc: string;

	/**
	 * PointClickCare's unique identifier for a unit within a facility
	 */
	unitId: number;

	/**
	 * The patient's last name
	 */
	lastName: string;

	/**
	 * The patient's middle name
	 */
	middleName: string;

	/**
	 * The patient's first name
	 */
	firstName: string;

	/**
	 * The preferred name for the patient, if available
	 */
	preferredName: string;

	/**
	 * The name prefix for the patient
	 */
	prefix: string;

	/**
	 * The name suffix for the patient
	 */
	suffix: string;

	/**
	 * Whether the patient is an outpatient
	 */
	outpatient: boolean;

	/**
	 * Patient's birth date (YYYY-MM-DD)
	 */
	birthDate: string;

	/**
	 * Date the patient was admitted (YYYY-MM-DD)
	 */
	admissionDate: string;

	/**
	 * Date and time the patient was admitted (YYYY-MM-DDTHH:MM:SS.sssZ)
	 */
	admissionDateTime: string;

	/**
	 * Date the patient was discharged (YYYY-MM-DD)
	 */
	dischargeDate: string;

	/**
	 * Date and time the patient was discharged (YYYY-MM-DDTHH:MM:SS.sssZ)
	 */
	dischargeDateTime: string;

	/**
	 * Date and time when the patient was deceased, if applicable
	 */
	deathDateTime?: string;

	/**
	 * Whether the patient is deceased
	 */
	deceased: boolean;

	/**
	 * Whether the patient is on a waiting list
	 */
	waitingList: boolean;

	/**
	 * URL to the patient's photo
	 */
	photoUrl: string;

	/**
	 * Whether the patient has a photo
	 */
	hasPhoto: boolean;

	/**
	 * Patient's gender: 'MALE', 'FEMALE' or 'UNKNOWN'
	 */
	gender: string;

	/**
	 * ISO 639-1 language code for the patient's primary language
	 */
	languageCode: string;

	/**
	 * Patient's primary language description
	 */
	languageDesc: string;

	/**
	 * Whether the patient is on a leave of absence from the facility
	 */
	isOnLeave: boolean;

	/**
	 * Patient's smoking status description
	 */
	smokingStatusDesc?: string;

	/**
	 * Patient's home phone number
	 */
	homePhone?: string;

	/**
	 * Patient's cell phone number
	 */
	cellPhone?: string;

	/**
	 * Patient's email address
	 */
	email?: string;

	/**
	 * Array of the patient's races (optional field)
	 */
	patientRaces?: PccPatientRace[];

	/**
	 * Array of patient contacts (optional field, requires includeOptionalFields=patientContacts)
	 */
	patientContacts?: PccPatientContact[];

	/**
	 * Array of user-defined fields for the patient (optional field, requires includeOptionalFields=userDefinedFields)
	 */
	userDefinedFields?: PccUserDefinedField[];

	/**
	 * Patient's previous mailing address (optional field)
	 */
	previousAddress?: PccPatientAddress;

	/**
	 * Patient's legal mailing address (optional field, requires includeOptionalFields=patientLegalMailingAddress)
	 */
	legalMailingAddress?: PccPatientAddress;

	@Expose()
	get fullName(): string {
		const coreName = [this.lastName, this.firstName].filter(Boolean).join(', ');

		const extensions = [this.middleName, this.suffix].filter(Boolean).join(' ');

		return [coreName, extensions].filter(Boolean).join(' ');
	}

	@Expose()
	get raceDescription(): string {
		return this.patientRaces?.map((r) => r.raceDesc).join(', ') || '';
	}

	constructor(partial?: Partial<PccPatient>) {
		if (partial) Object.assign(this, partial);
		if (this.patientRaces) this.patientRaces = this.patientRaces.map((r) => new PccPatientRace(r));
		if (this.patientContacts) this.patientContacts = this.patientContacts.map((c) => new PccPatientContact(c));
		if (this.userDefinedFields) this.userDefinedFields = this.userDefinedFields.map((f) => new PccUserDefinedField(f));
		if (this.previousAddress) this.previousAddress = new PccPatientAddress(this.previousAddress);
		if (this.legalMailingAddress) this.legalMailingAddress = new PccPatientAddress(this.legalMailingAddress);
	}

	getUserDefinedField(name: string): string {
		return this.userDefinedFields?.find((f) => f.name.toLowerCase() === name.toLowerCase())?.description || '';
	}
}
